import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Ricerca } from './anagrafiche.js';
import { chiama } from './cliente.js';

export type Validita = 'active' | 'future' | 'expired';

export interface RigaListino {
  id: number;
  name: string;
  customerId: number | null;
  customer: string | null;
  countryId: number | null;
  country: string | null;
  startDate: string | null;
  endDate: string | null;
  validity: Validita;
  flatDiscountPercentage: string | null;
  shippingMethod: string;
  lines: number;
  archived: boolean;
}

export interface PrezzoDestinazione {
  id: number;
  addressId: number;
  address: string | null;
  city: string | null;
  country: string | null;
  priceExw: string;
  costDdp: string;
  discountPercentage1: string | null;
  discountPercentage2: string | null;
  discountPercentage3: string | null;
  tax: string | null;
  startDate: string | null;
  endDate: string | null;
}

export interface RigaPrezzo {
  id: number;
  articleId: number;
  sku: string;
  bcNumber: string | null;
  description: string;
  priceExw: string;
  costDdp: string;
  discountPercentage1: string | null;
  discountPercentage2: string | null;
  discountPercentage3: string | null;
  taxId: number;
  tax: string | null;
  crossReference: string | null;
  destinations: PrezzoDestinazione[];
}

export interface Listino {
  id: number;
  name: string;
  customerId: number | null;
  customer: string | null;
  customerBcCode: string | null;
  countryId: number | null;
  country: string | null;
  startDate: string | null;
  endDate: string | null;
  validity: Validita;
  flatDiscountPercentage: string | null;
  shippingMethod: string;
  notes: string | null;
  archived: boolean;
  lines: RigaPrezzo[];
}

interface Pagina {
  rows: RigaListino[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export const RICERCA_LISTINI: Ricerca = {
  page: 1,
  size: 50,
  // il più recente in cima: è quello che sta per entrare in vigore
  sort: 'startDate',
  dir: 'desc',
  q: '',
  archived: false,
  filters: {},
};

function queryString(r: Ricerca, extra: Record<string, string> = {}) {
  return new URLSearchParams({
    page: String(r.page),
    size: String(r.size),
    sort: r.sort,
    dir: r.dir,
    ...(r.q ? { q: r.q } : {}),
    ...(r.archived ? { archived: 'true' } : {}),
    ...Object.fromEntries(
      Object.entries(r.filters ?? {})
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => [k, String(v)]),
    ),
    ...extra,
  }).toString();
}

export function useListini(ricerca: Ricerca) {
  return useQuery({
    queryKey: ['listini', ricerca],
    queryFn: () => chiama<Pagina>(`/api/price-lists?${queryString(ricerca)}`),
    placeholderData: (precedente) => precedente,
  });
}

/** Le linguette, con la ricerca e i filtri dell'elenco tranne la validità. */
export function useConteggiValidita(ricerca: Ricerca) {
  const { validity: _fuori, ...filtri } = ricerca.filters ?? {};
  const senzaValidita = { ...ricerca, filters: filtri, page: 1 };
  return useQuery({
    queryKey: ['listini-conteggi', senzaValidita],
    queryFn: () =>
      chiama<{ counts: Record<Validita, number> }>(
        `/api/price-lists/counts?${queryString(senzaValidita)}`,
      ),
    placeholderData: (precedente) => precedente,
  });
}

export function useListino(id: number) {
  return useQuery({
    queryKey: ['listino', id],
    queryFn: () => chiama<Listino>(`/api/price-lists/${id}`),
    enabled: Number.isInteger(id) && id > 0,
  });
}

export function useDefinizioneFiltriListini() {
  return useQuery({
    queryKey: ['filtri-listini'],
    queryFn: () =>
      chiama<{
        filters: {
          key: string;
          label: string;
          type: 'text' | 'list' | 'yesNo' | 'choice' | 'date';
          options?: string;
          values?: { value: string; label: string }[];
        }[];
        options: Record<string, { id: string | number; label: string }[]>;
      }>('/api/price-lists/filters'),
    staleTime: 10 * 60_000,
  });
}

export function useDuplicaListino(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: { name: string; startDate: string | null; endDate: string | null }) =>
      chiama<{ id: number; lines: number; destinations: number }>(
        `/api/price-lists/${id}/duplicate`,
        { metodo: 'POST', corpo: dati },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['listini'] });
      void client.invalidateQueries({ queryKey: ['listini-conteggi'] });
    },
  });
}

export function useSalvaTestataListino(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: Record<string, unknown>) =>
      chiama<Listino>(`/api/price-lists/${id}`, { metodo: 'PATCH', corpo: dati }),
    onSuccess: (listino) => {
      client.setQueryData(['listino', id], listino);
      void client.invalidateQueries({ queryKey: ['listini'] });
    },
  });
}

/**
 * Si salva una riga alla volta: chi corregge tre prezzi su centoquindici non
 * deve rimandare indietro centoquindici righe e rischiare di sovrascrivere il
 * lavoro fatto nel frattempo da qualcun altro.
 */
export function useSalvaRigaListino(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ lineId, ...dati }: Record<string, unknown> & { lineId: number }) =>
      chiama<Listino>(`/api/price-lists/${id}/lines/${lineId}`, { metodo: 'PATCH', corpo: dati }),
    onSuccess: (listino) => client.setQueryData(['listino', id], listino),
  });
}

export interface OpzioniListino {
  customers: { id: number; label: string }[];
  countries: { id: number; label: string }[];
  taxes: { id: number; label: string; isDefault: boolean }[];
  shippingMethods: { value: string; label: string }[];
}

export function useOpzioniListini() {
  return useQuery({
    queryKey: ['opzioni-listini'],
    queryFn: () => chiama<OpzioniListino>('/api/price-lists/options'),
    staleTime: 10 * 60_000,
  });
}

/**
 * Un listino nuovo, vuoto. Nasce da qui e non da una copia quando il cliente
 * un listino prima non ce l'aveva.
 */
export function useCreaListino() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: Record<string, unknown>) =>
      chiama<{ id: number }>('/api/price-lists', { metodo: 'POST', corpo: dati }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['listini'] });
      void client.invalidateQueries({ queryKey: ['listini-conteggi'] });
    },
  });
}

/** Aggiunge un articolo al listino. */
export function useAggiungiRigaListino(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: Record<string, unknown>) =>
      chiama<{ id: number }>(`/api/price-lists/${id}/lines`, { metodo: 'POST', corpo: dati }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['listino', id] });
      void client.invalidateQueries({ queryKey: ['listini'] });
    },
  });
}

export function useTogliRigaListino(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (lineId: number) =>
      chiama<{ deleted: boolean }>(`/api/price-lists/${id}/lines/${lineId}`, { metodo: 'DELETE' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['listino', id] });
      void client.invalidateQueries({ queryKey: ['listini'] });
    },
  });
}

/** Gli indirizzi su cui si può fare un prezzo per destinazione. */
export function useIndirizziListino(id: number, attivo: boolean) {
  return useQuery({
    queryKey: ['listino-indirizzi', id],
    queryFn: () =>
      chiama<{ addresses: { id: number; label: string; city: string | null; country: string | null }[] }>(
        `/api/price-lists/${id}/addresses`,
      ),
    enabled: attivo && Number.isInteger(id) && id > 0,
    staleTime: 5 * 60_000,
  });
}

export function useAggiungiDestinazione(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ lineId, ...dati }: Record<string, unknown> & { lineId: number }) =>
      chiama<{ id: number }>(`/api/price-lists/${id}/lines/${lineId}/destinations`, {
        metodo: 'POST',
        corpo: dati,
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['listino', id] }),
  });
}

/** Corregge sul posto un prezzo per destinazione: è il lavoro di ogni trimestre. */
export function useCorreggiDestinazione(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      lineId,
      destId,
      ...dati
    }: Record<string, unknown> & { lineId: number; destId: number }) =>
      chiama<unknown>(`/api/price-lists/${id}/lines/${lineId}/destinations/${destId}`, {
        metodo: 'PATCH',
        corpo: dati,
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['listino', id] }),
  });
}

export function useTogliDestinazione(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ lineId, destId }: { lineId: number; destId: number }) =>
      chiama<{ deleted: boolean }>(
        `/api/price-lists/${id}/lines/${lineId}/destinations/${destId}`,
        { metodo: 'DELETE' },
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['listino', id] }),
  });
}

/** Il contenuto di un listino, in Excel: righe, prezzi per destinazione e testata. */
export function esportaListino(id: number): void {
  window.location.href = `/api/price-lists/${id}/export`;
}

export function esportaListini(ricerca: Ricerca, formato: 'xlsx' | 'csv'): void {
  window.location.href = `/api/price-lists/export?${queryString(ricerca, { format: formato })}`;
}
